/* eslint-disable no-console */
import React, { useCallback, useState } from "react";
import { Container, Grid, Typography } from "@material-ui/core";
import {
	DataGridPro,
	GridColumns,
	GridToolbarColumnsButton,
	GridToolbarDensitySelector,
	GridToolbarExport,
	GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import PageBreadcrumbs from "../components/Breadcrumbs/PageBreadcrumbs";
import { breadcrumbNode } from "../components/Breadcrumbs/PageBreadcrumbs.d";
import { useBatchUpdateInventory } from "../api/replenishmentApi";
import { Box, Button, Tooltip, Snackbar, Alert } from "@mui/material";
import { DownloadOutlined, FileUploadOutlined } from "@mui/icons-material";
import useInitialAppData from "../Hook/UseInitialAppData";
import LoadingIndicator from "../components/LoadingIndicator/LoadingIndicator";

interface DisplayRequiredRow {
	id: string;
	sku: string;
	locationId: number;
	displayRequired: boolean;
}

type SnackbarState = { open: boolean; message: string; severity: "success" | "error"; autoHide: boolean } | null;

const DisplayRequired: React.FC = () => {
	const breadcrumbNodes: breadcrumbNode[] = [{ name: "Update Display Required" }];

	// Limits are sourced from InitialAppData (driven by the BFF's
	// DisplayRequired:BatchLimit / DisplayRequired:ImportCSVLimit settings).
	// Guard against a stale/older BFF response that omits uploadLimits, so the
	// problem surfaces as an obvious console error rather than silently
	// allowing imports of any size.
	const initialAppData = useInitialAppData();
	const limits = initialAppData.uploadLimits?.displayRequired;
	if (!limits) {
		// eslint-disable-next-line no-console
		console.error(
			"InitialAppData response is missing uploadLimits.displayRequired. " +
				"The BFF may be running an older build, or local.settings.json may not have been picked up (restart the function host after changing it). " +
				"Falling back to defaults (batchLimit=1000, importCSVLimit=10000)."
		);
	}
	const batchLimit = limits?.batchLimit ?? 1000;
	const importCSVLimit = limits?.importCSVLimit ?? 10000;

	const [rows, setRows] = useState<DisplayRequiredRow[]>([]);
	// Info banner shown above the grid when the imported CSV will be sent in
	// multiple batches (rows.length > batchLimit but <= importCSVLimit).
	const [batchInfoMessage, setBatchInfoMessage] = useState<string | null>(null);
	const [snackbar, setSnackbar] = useState<SnackbarState>(null);
	const [{ loading: isSaving }, batchUpdateInventory] = useBatchUpdateInventory();

	const columns: GridColumns = [
		{ field: "sku", headerName: "SKU", width: 150, editable: false },
		{ field: "locationId", headerName: "Location ID", type: "number", width: 130, editable: false },
		{ field: "displayRequired", headerName: "Display Required", type: "boolean", width: 150, editable: true },
	];

	const handleDownloadTemplate = useCallback(() => {
		const csvContent = "Sku,LocationId,DisplayRequired\n";
		const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", "display_required_template.csv");
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}, []);

	const handleImport = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (file) {
				const reader = new FileReader();
				reader.onload = (e) => {
					const text = e.target?.result as string;
					const lines = text.split("\n");
					const headers = lines[0]
						.toLowerCase()
						.split(",")
						.map((h) => h.trim());

					// Validate headers
					const expectedHeaders = ["sku", "locationid", "displayrequired"];
					const isValidHeader = expectedHeaders.every((header) => headers.includes(header));

					if (!isValidHeader) {
						setSnackbar({
							open: true,
							message: "Invalid CSV format. Expected headers: Sku,LocationId,DisplayRequired",
							severity: "error",
							autoHide: true,
						});
						return;
					}

					const newRows: DisplayRequiredRow[] = [];
					for (let i = 1; i < lines.length; i++) {
						const line = lines[i].trim();
						if (line) {
							const values = line.split(",").map((v) => v.trim());
							if (values.length >= 3) {
								const displayRequiredValue = values[2]?.toLowerCase();
								const skuValue = values[0];
								const locationIdValue = values[1];

								// Validate boolean value
								if (displayRequiredValue !== "true" && displayRequiredValue !== "false") {
									setSnackbar({
										open: true,
										message: `Invalid DisplayRequired value at row ${i + 1}. Must be 'true' or 'false'.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}
								// Validate locationId value
								const locationIdNumber = Number(locationIdValue);
								if (!Number.isInteger(locationIdNumber) || locationIdNumber <= 0) {
									setSnackbar({
										open: true,
										message: `Invalid LocationId value at row ${i + 1}. Must be a positive number.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}

								// Validate SKU value
								const skuNumber = Number(skuValue);
								if (!Number.isInteger(skuNumber) || skuNumber <= 0) {
									setSnackbar({
										open: true,
										message: `Invalid SKU value at row ${i + 1}. Must be a positive number.`,
										severity: "error",
										autoHide: true,
									});
									return;
								}

								const row: DisplayRequiredRow = {
									id: `${values[0]}-${values[1]}`,
									sku: values[0],
									locationId: parseInt(values[1]),
									displayRequired: displayRequiredValue === "true",
								};
								newRows.push(row);
							}
						}
					}

					// Row count limits (BHS-544). Block hard at importCSVLimit; show an
					// informational banner when the import will be split into batches.
					if (newRows.length > importCSVLimit) {
						setRows([]);
						setBatchInfoMessage(null);
						setSnackbar({
							open: true,
							message: `Imports must not exceed ${importCSVLimit}`,
							severity: "error",
							autoHide: true,
						});
						return;
					}

					if (newRows.length > batchLimit) {
						setBatchInfoMessage(`Uploads will be saved in batches of ${batchLimit}.`);
					} else {
						setBatchInfoMessage(null);
					}

					setRows(newRows);
				};
				reader.readAsText(file);
			}
			// Reset the input value so the same file can be uploaded again
			event.target.value = "";
		},
		[batchLimit, importCSVLimit]
	);

	const handleSave = useCallback(async () => {
		const updateRequests = rows.map((row) => ({
			sku: row.sku,
			locationId: row.locationId,
			displayRequired: row.displayRequired,
		}));

		try {
			const response = await batchUpdateInventory(updateRequests);
			const successful = response.data?.successfulBatches ?? [];
			const failed = response.data?.failedBatches ?? [];

			if (failed.length === 0) {
				// All batches succeeded (or the BFF returned no batches at all, which
				// shouldn't happen but is treated as success-equivalent).
				setSnackbar({
					open: true,
					message: "Display required updates saved successfully!",
					severity: "success",
					autoHide: true,
				});
				return;
			}

			// At least one batch failed — keep the message visible until the user
			// dismisses it so they can read which batches succeeded and which
			// need re-uploading (BHS-544 AC).
			const failedList = failed.join(", ");
			const successList = successful.join(", ");
			const message =
				successful.length === 0
					? `Save failed. All ${failed.length} batch(es) were rejected (batches ${failedList}). Please retry the upload.`
					: `Saved batches ${successList}; failed batches ${failedList}. Please retry the upload.`;

			setSnackbar({
				open: true,
				message,
				severity: "error",
				autoHide: false,
			});
		} catch (error: any) {
			console.log(`${error?.message} | ${error?.response?.data}`);
			setSnackbar({
				open: true,
				message: error?.message
					? `Error saving display required updates: ${error.message}`
					: "Error saving display required updates. Please try again.",
				severity: "error",
				autoHide: false,
			});
		}
	}, [rows, batchUpdateInventory]);

	const handleRowEdit = useCallback((updatedRow: DisplayRequiredRow) => {
		setRows((prevRows) => prevRows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
		return updatedRow;
	}, []);

	const CustomToolbar = () => {
		return (
			<Box sx={{ display: "flex", alignItems: "center", p: 1, justifyContent: "space-between" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<GridToolbarColumnsButton />
					<GridToolbarFilterButton />
					<GridToolbarDensitySelector />
					<GridToolbarExport />
					<Tooltip title="Download Import Template">
						<Button size="small" startIcon={<DownloadOutlined />} onClick={handleDownloadTemplate} sx={{ height: 36 }}>
							Template
						</Button>
					</Tooltip>
					<input
						id="display-required-import-input"
						type="file"
						accept=".csv"
						style={{ display: "none" }}
						onChange={handleImport}
					/>
					<label htmlFor="display-required-import-input">
						<Button size="small" startIcon={<FileUploadOutlined />} component="span" sx={{ height: 36 }}>
							Import
						</Button>
					</label>
					{rows.length > 0 && (
						<Button color="primary" onClick={() => handleSave()} disabled={isSaving} size="small" sx={{ height: 36 }}>
							Save All
						</Button>
					)}
				</Box>
			</Box>
		);
	};

	return (
		<Container maxWidth={false}>
			<Grid container spacing={6}>
				<Grid container item direction="row">
					<Grid item xs={12} sm={8}>
						<PageBreadcrumbs nodes={breadcrumbNodes} />
					</Grid>
				</Grid>
				<Grid container>
					<Typography variant="h4">Display Required Updates</Typography>
				</Grid>
				<Grid container>
					<Typography>Upload a CSV file to set display required flags or download the template to get started.</Typography>
				</Grid>
				{batchInfoMessage && (
					<Grid item xs={12}>
						<Alert severity="info">{batchInfoMessage}</Alert>
					</Grid>
				)}
				<Grid item xs={12}>
					<DataGridPro
						autoHeight
						density="standard"
						rows={rows}
						columns={columns}
						paginationMode="client"
						rowsPerPageOptions={[25, 50, 100]}
						pagination
						processRowUpdate={handleRowEdit}
						onProcessRowUpdateError={(error) => console.error(error)}
						components={{
							Toolbar: CustomToolbar,
						}}
						initialState={{
							pagination: {
								pageSize: 25,
							},
							sorting: {
								sortModel: [{ field: "sku", sort: "asc" }],
							},
						}}
					/>
				</Grid>
			</Grid>
			{/* Blocking save modal — reuses the existing LoadingIndicator (no close
			    handler / backdrop click handler), so the page is non-interactive
			    until the BFF response is received (BHS-544). */}
			<LoadingIndicator open={isSaving} content="Saving – please do not close or refresh this page" />
			<Snackbar
				open={!!snackbar?.open}
				autoHideDuration={snackbar?.autoHide ? 5000 : null}
				onClose={(_event, reason) => {
					// Don't auto-close the partial-failure outcome on backdrop click —
					// the user needs to read which batches need to be re-uploaded.
					if (!snackbar?.autoHide && reason === "clickaway") return;
					setSnackbar((prev) => (prev ? { ...prev, open: false } : null));
				}}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar((prev) => (prev ? { ...prev, open: false } : null))}
					severity={snackbar?.severity}
					sx={{
						width: "100%",
						bgcolor: snackbar?.severity === "success" ? "success.main" : "error.main",
						color: "#fff",
					}}
				>
					{snackbar?.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default DisplayRequired;
